import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import CTASection from '@/pages/home/components/CTASection';
import { articleData, articleComments } from '@/mocks/article';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articleData[articleId] || articleData[1];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cgshe.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: 'http://cgshe.cn/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 黑料吃瓜网`}
        description={article.summary}
        keywords={`黑料吃瓜网,${article.tags.join(',')}`}
        canonical={`http://cgshe.cn/article/${id}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={articleSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main>
        <article className="pt-20 md:pt-24">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/category/all" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">{article.category}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
              </nav>
            </div>
          </div>

          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="flex-1 lg:max-w-[45%] flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    {article.summary}
                  </p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                    </span>
                  </div>
                </div>

                <div className="flex-1 lg:max-w-[55%]">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full aspect-[2/1] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom space-y-4">
                {article.content.map((paragraph, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed indent-8">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </article>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10">
                <span className="inline-block px-4 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  最新热评
                </span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-tight">
                  千万网友
                  <br />
                  在线吃瓜
                </h2>
              </div>

              <div className="columns-1 md:columns-2 lg:columns-3 gap-5 md:gap-6">
                {articleComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="break-inside-avoid mb-5 md:mb-6 bg-background-50 border border-background-200/70 rounded-2xl p-5 md:p-6 hover:border-accent-200/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <span className="font-heading text-accent-600 text-sm">{comment.avatar}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{comment.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-foreground-400">{comment.handle}</p>
                          <span className="text-xs text-foreground-300">·</span>
                          <p className="text-xs text-foreground-400">{comment.time}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      &ldquo;{comment.comment}&rdquo;
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
                        <i className="ri-heart-line"></i>
                        <span>{comment.likes}</span>
                      </button>
                      <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
                        <i className="ri-chat-1-line"></i>
                        <span>回复</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CTASection />

        {article.relatedArticles && article.relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-50">
            <div className="px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {article.relatedArticles.map((relatedId) => {
                    const relArticle = articleData[relatedId];
                    if (!relArticle) return null;
                    return (
                      <Link
                        key={relatedId}
                        to={`/article/${relatedId}`}
                        className="group block rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={relArticle.image}
                            alt={relArticle.title}
                            title={relArticle.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-sm md:text-base text-foreground-950 line-clamp-2 leading-snug">
                            {relArticle.title}
                          </h3>
                          <p className="mt-2 text-xs text-foreground-400">{relArticle.date}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}